import React, { useState, useEffect } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, onSnapshot, query, where, getDocs, doc, updateDoc, writeBatch } from './firestore-compat';
import CommentsModal from './CommentsModal';
import AddQualidadeTaskManualModal from './AddQualidadeTaskManualModal';
import { addDoc, deleteDoc } from './firestore-compat'; // Ensure addDoc/deleteDoc are imported

const STATUS_OPTIONS = ['Pendente', 'Em andamento', 'Feito', 'Não Realizado'];

const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setHours(0, 0, 0, 0);
    return new Date(d.setDate(diff));
};

const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

// Cálculo de duração em formato HH:MM
const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    const startDate = new Date(`2000-01-01T${start}`);
    const endDate = new Date(`2000-01-01T${end}`);
    let diff = endDate - startDate;
    if (diff < 0) return null;

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
};

const MINHAS_TAREFAS_DESATIVADO = true;

function MinhasTarefas() {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [myTasks, setMyTasks] = useState([]);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [isManualTaskModalOpen, setIsManualTaskModalOpen] = useState(false);
    const [editingManualTask, setEditingManualTask] = useState(null);

    const currentUser = auth.currentUser;

    const startOfWeek = getStartOfWeek(currentDate);
    const endOfWeek = addDays(startOfWeek, 4);

    useEffect(() => {
        if (MINHAS_TAREFAS_DESATIVADO) { setLoading(false); return; }
        const fetchUserData = async () => {
            if (!currentUser) return;

            const qU = query(collection(db, 'users'), where('email', '==', currentUser.email));
            const snapU = await getDocs(qU);

            if (!snapU.empty) {
                const uData = snapU.docs[0].data();
                setUserData(uData);
                const myName = uData.agendaName || uData.name;

                // Query para buscar tarefas onde o usuário é auditor/responsável
                // Por enquanto focando em tarefas_homecare e tarefas_qualidade se houver
                // O padrão parece ser string em "auditor" ou array em "auditores"

                // NOTA: Firestore não tem "OR" fácil entre campos diferentes sem índices complexos.
                // Vamos buscar tudo e filtrar no cliente por enquanto ou criar índices se necessário.
                // Como volume por usuário não é gigante, filtrar no cliente as coleções é mais seguro agora.

                const collectionsToFetch = [getCollectionName('tarefas_homecare')];

                const unsubscribes = collectionsToFetch.map(collName => {
                    return onSnapshot(collection(db, collName), (snapshot) => {
                        const allTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), sourceColl: collName }));

                        const myFilteredTasks = allTasks.filter(task => {
                            const auds = task.auditores || (task.auditor ? [task.auditor] : []);
                            return auds.includes(myName);
                        });

                        setMyTasks(prev => {
                            // Remove anteriores dessa coleção
                            const others = prev.filter(t => t.sourceColl !== collName);
                            return [...others, ...myFilteredTasks];
                        });
                        setLoading(false);
                    });
                });

                return () => unsubscribes.forEach(u => u());
            } else {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [currentUser]);

    const handlePrevWeek = () => setCurrentDate(addDays(currentDate, -7));
    const handleNextWeek = () => setCurrentDate(addDays(currentDate, 7));

    const handleUpdateStatus = async (task, newStatus) => {
        const batch = writeBatch(db);
        const taskRef = doc(db, task.sourceColl, task.id);

        const updateData = { status: newStatus };
        if (newStatus === 'Feito') {
            updateData.dataConclusao = new Date().toISOString().split('T')[0];
        } else {
            updateData.dataConclusao = null;
        }

        batch.update(taskRef, updateData);

        if (task.agendaId) {
            // Tenta adivinhar a coleção de agenda baseada na de tarefas
            const agendaColl = task.sourceColl === getCollectionName('tarefas_homecare') ? getCollectionName('auditorias_homecare') : getCollectionName('auditorias');
            const agendaRef = doc(db, agendaColl, task.agendaId);
            batch.update(agendaRef, updateData);
        }

        await batch.commit();
    };

    const handleAddComment = async (taskId, commentText) => {
        const task = myTasks.find(t => t.id === taskId);
        if (!task) return;

        const taskRef = doc(db, task.sourceColl, taskId);
        const currentComments = task.comments || [];
        const newComment = {
            text: commentText,
            userName: userData.name || userData.email,
            timestamp: Date.now()
        };

        await updateDoc(taskRef, { comments: [...currentComments, newComment] });
        // Local update handled by snapshot, but if selecting task:
        if (selectedTask && selectedTask.id === taskId) {
            setSelectedTask(prev => ({ ...prev, comments: [...currentComments, newComment] }));
        }
    };

    const openCommentsModal = (task) => {
        setSelectedTask(task);
        setIsCommentsModalOpen(true);
    };

    const handleOpenManualTaskModal = () => {
        setEditingManualTask(null);
        setIsManualTaskModalOpen(true);
    };

    const handleAddManualTask = async (newTask, agendarNaAgenda) => {
        // Defaulting to tarefas_homecare for now since that is what we fetch
        const targetColl = getCollectionName('tarefas_homecare');

        if (!agendarNaAgenda) {
            await addDoc(collection(db, targetColl), {
                ...newTask,
                status: 'Pendente',
                reagendadoVezes: 0,
                auditores: [userData.agendaName || userData.name] // Assign to self
            });
            return;
        }

        const batch = writeBatch(db);
        const tarefaRef = doc(collection(db, targetColl));
        const agendaRef = doc(collection(db, getCollectionName('auditorias_homecare')));

        batch.set(tarefaRef, {
            ...newTask,
            status: 'Pendente',
            reagendadoVezes: 0,
            agendaId: agendaRef.id,
            auditores: [userData.agendaName || userData.name]
        });

        batch.set(agendaRef, {
            ...newTask,
            descricao: newTask.titulo,
            auditoria: newTask.titulo,
            categoria: 'Tarefa Interna',
            status: 'Agendado',
            reagendadoVezes: 0,
            tarefaId: tarefaRef.id,
            auditores: [userData.agendaName || userData.name],
            unidade: 'Escritório' // Default location
        });

        await batch.commit();
    };

    const handleUpdateManualTask = async (originalTask, updatedData) => {
        // Only allow updating if it is a manual task (check logic if needed)
        const batch = writeBatch(db);
        const taskRef = doc(db, originalTask.sourceColl, originalTask.id);

        let reagendadoVezes = originalTask.reagendadoVezes || 0;
        if (originalTask.data !== updatedData.data) {
            reagendadoVezes++;
        }

        batch.update(taskRef, { ...updatedData, reagendadoVezes });

        if (originalTask.agendaId) {
            const agendaColl = originalTask.sourceColl === getCollectionName('tarefas_homecare') ? getCollectionName('auditorias_homecare') : getCollectionName('auditorias');
            const agendaRef = doc(db, agendaColl, originalTask.agendaId);

            batch.update(agendaRef, {
                data: updatedData.data,
                horario: updatedData.horario,
                descricao: updatedData.titulo,
                auditoria: updatedData.titulo
            });
        }

        await batch.commit();
    };

    const weekDays = Array.from({ length: 5 }).map((_, i) => addDays(startOfWeek, i));
    const groupedTasks = weekDays.map(day => {
        const dayString = day.toISOString().split('T')[0];
        const tasksOfDay = myTasks
            .filter(task => task.data === dayString)
            .sort((a, b) => (a.horario || '00:00').localeCompare(b.horario || '00:00'));
        return { day, tasks: tasksOfDay };
    }).filter(group => group.tasks.length > 0);

    if (loading) return <div>Carregando tarefas...</div>;

    if (MINHAS_TAREFAS_DESATIVADO) {
        return (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
                <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Minhas Tarefas desativado</h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
            </div>
        );
    }

    return (
        <div className="tarefas-qualidade-container">
            <div className="page-header" style={{ marginBottom: '20px', position: 'relative' }}>
                <h2 style={{ color: '#555', margin: 0 }}>Minhas Tarefas</h2>
                <p className="text-muted">Acompanhe as tarefas atribuídas a você.</p>

                <button
                    className="novo-agendamento-btn"
                    style={{ position: 'absolute', top: '20px', right: '30px' }}
                    onClick={handleOpenManualTaskModal}
                >
                    + Nova Tarefa
                </button>
            </div>

            <CommentsModal
                isOpen={isCommentsModalOpen}
                onClose={() => setIsCommentsModalOpen(false)}
                task={selectedTask}
                onAddComment={(ignored, taskId, text) => handleAddComment(taskId, text)}
            />

            <main className="tarefas-qualidade-main">
                <div className="agenda-controls">
                    <div className="week-navigation">
                        <button onClick={handlePrevWeek}>&lt; Semana Anterior</button>
                        <span>{`${startOfWeek.toLocaleDateString('pt-BR')} - ${endOfWeek.toLocaleDateString('pt-BR')}`}</span>
                        <button onClick={handleNextWeek}>Próxima Semana &gt;</button>
                    </div>
                </div>

                <div className="qualidade-task-list">
                    {groupedTasks.length > 0 ? (
                        groupedTasks.map(({ day, tasks }) => (
                            <div key={day.toISOString()} className="day-group">
                                <h3 className="day-group-header">{day.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' })}</h3>
                                <div className="qualidade-task-card-header">
                                    <div className="qualidade-task-time">Horário</div>
                                    <div className="qualidade-task-details">Tarefa</div>
                                    <div className="qualidade-task-conclusao">Tempo</div>
                                    <div className="qualidade-task-status">Status</div>
                                    <div className="qualidade-task-actions">Ações</div>
                                </div>
                                {tasks.map(task => {
                                    const duration = calculateDuration(task.horaInicioReal, task.horaFimReal);

                                    return (
                                        <div
                                            key={task.id}
                                            className="qualidade-task-card"
                                            style={{ borderLeftColor: '#00897b' }}
                                        >
                                            <div className="qualidade-task-time">{task.horario}</div>
                                            <div className="qualidade-task-details">
                                                <p className="qualidade-task-title">{task.titulo}</p>
                                                <p className="qualidade-task-meta">
                                                    <span className="badge bg-secondary">
                                                        {task.sourceColl === 'tarefas_homecare' ? 'Homecare' :
                                                            (task.sourceColl === 'agenda_pessoal' || task.isPersonal ? 'Pessoal' : 'Qualidade')}
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="qualidade-task-conclusao">
                                                {duration ? (
                                                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>{duration}</span>
                                                ) : (
                                                    <span style={{ color: '#999' }} >--</span>
                                                )}
                                            </div>
                                            <div className="qualidade-task-status">
                                                <select
                                                    value={task.status || 'Pendente'}
                                                    className={`status-select status-${(task.status || 'pendente').replace(/\s+/g, '-').toLowerCase()}`}
                                                    onChange={(e) => handleUpdateStatus(task, e.target.value)}
                                                >
                                                    {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                            </div>
                                            <div className="qualidade-task-actions">
                                                <button className="comment-btn" onClick={() => openCommentsModal(task)}>
                                                    💬 <span className="comment-count">{task.comments?.length || 0}</span>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    ) : (
                        <div className="text-center p-5">
                            <h4 style={{ color: '#aaa' }}>Nenhuma tarefa encontrada para esta semana.</h4>
                        </div>
                    )}
                </div>
            </main>

            <AddQualidadeTaskManualModal
                isOpen={isManualTaskModalOpen}
                onClose={() => setIsManualTaskModalOpen(false)}
                onAddTask={handleAddManualTask}
                onUpdateTask={(task, data) => handleUpdateManualTask(task, data)}
                editingTask={editingManualTask}
                isPersonal={true}
                userName={userData?.agendaName || userData?.name}
                userRole={userData?.role}
            />
        </div>
    );
}

export default MinhasTarefas;
